import React from 'react';
import { FaUserCircle, FaCar, FaCloud, FaClock, FaTachometerAlt, FaMapMarkerAlt, FaCalculator } from 'react-icons/fa';

const HeaderSection = ({ 
    displayName, 
    isDriver, 
    isPassenger, 
    isAdmin, 
    isSuperAdmin, 
    vehicleModel, 
    distanceToday, 
    lastLocation, 
    gpsOnline, 
    setGpsOnline,
    gpsError,
    clearGpsError,
    setShowCalcModal 
}) => (
    <div className="welcome-banner p-3 mb-3 rounded-16 shadow-sm border-0 position-relative overflow-hidden" 
         style={{ 
             background: 'linear-gradient(135deg, #1e1e1e 0%, var(--bry-bg-secondary) 100%)', 
             color: 'var(--bry-text-main)',
             border: '1px solid rgba(255, 255, 255, 0.05)'
         }}>
        {/* Ambient light effect */}
        <div className="position-absolute" style={{
            top: '-50px',
            right: '-50px',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'rgba(14, 171, 176, 0.15)',
            filter: 'blur(40px)',
            zIndex: 0,
            pointerEvents: 'none'
        }} />
        
        <div className="d-flex align-items-center justify-content-between position-relative flex-wrap gap-2" style={{ zIndex: 1 }}>
            <div className="d-flex align-items-center gap-3">
                <div className="user-avatar-circle d-flex align-items-center justify-content-center" 
                     style={{ 
                         background: 'rgba(14, 171, 176, 0.12)', 
                         borderRadius: '50%', 
                         width: '48px', 
                         height: '48px',
                         border: '1px solid rgba(14, 171, 176, 0.2)'
                     }}>
                    <FaUserCircle size={28} color="var(--bry-accent)" />
                </div>
                <div>
                    <h1 className="welcome-name m-0" style={{ fontSize: '1.2rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--bry-text-main)' }}>
                        {(isAdmin || isSuperAdmin) && `${displayName} (Admin)`}
                        {isDriver && !isAdmin && !isSuperAdmin && `Conductor: ${displayName}`}
                        {isPassenger && !isDriver && !isAdmin && !isSuperAdmin && `${displayName} (Cliente)`}
                        {!isDriver && !isAdmin && !isSuperAdmin && !isPassenger && displayName}
                    </h1>
                    <p className="welcome-sub m-0 opacity-75" style={{ fontSize: '0.78rem', color: '#eaeaea' }}>
                        {isDriver     && '¿Listo para ganar hoy? 🚗'}
                        {isPassenger  && '¿Qué necesitas hoy? 📦'}
                        {(isAdmin || isSuperAdmin) && 'Panel de control administrativo 🛠️'}
                    </p>
                </div>
            </div>

            {/* Acciones Rápidas: GPS y Calculadora */}
            <div className="d-flex align-items-center gap-2">
                <button
                    type="button"
                    onClick={() => setGpsOnline && setGpsOnline(!gpsOnline)}
                    className={`btn btn-sm rounded-pill px-3 py-1 d-flex align-items-center gap-2 fw-bold text-white shadow-sm border ${gpsOnline ? 'btn-success border-success' : 'btn-secondary bg-opacity-75 border-secondary'}`}
                    style={{ fontSize: '0.78rem', minHeight: '34px' }}
                    title={gpsOnline ? "Haz clic para desactivar GPS" : "Haz clic para activar GPS"}
                >
                    <span 
                        style={{ 
                            width: '9px', 
                            height: '9px', 
                            borderRadius: '50%', 
                            backgroundColor: gpsOnline ? '#00ff00' : '#ffffff',
                            boxShadow: gpsOnline ? '0 0 8px #00ff00' : 'none'
                        }} 
                    />
                    <span>{gpsOnline ? 'GPS ACTIVO' : 'ACTIVAR GPS'}</span>
                </button>

                {setShowCalcModal && (
                    <button
                        type="button"
                        onClick={() => setShowCalcModal(true)}
                        className="btn btn-sm btn-outline-info rounded-pill px-3 py-1 d-flex align-items-center gap-1.5 fw-bold text-white shadow-sm"
                        style={{ fontSize: '0.78rem', minHeight: '34px', borderColor: 'rgba(14, 171, 176, 0.4)' }}
                    >
                        <FaCalculator size={12} color="var(--bry-accent)" />
                        <span>Calculadora</span>
                    </button>
                )}
            </div>
        </div>

        {gpsError && (
            <div className="alert alert-warning py-1.5 px-3 mt-2 mb-0 d-flex justify-content-between align-items-center rounded-12 position-relative" style={{ zIndex: 1, fontSize: '0.76rem' }}>
                <span>⚠️ {gpsError}</span>
                {clearGpsError && (
                    <button type="button" className="btn btn-sm p-0 ms-2 text-dark fw-bold border-0 bg-transparent" onClick={clearGpsError}>✕</button>
                )}
            </div>
        )}

        <div className="d-flex align-items-center justify-content-between mt-3 pt-2 border-top position-relative" 
             style={{ zIndex: 1, borderColor: 'rgba(255, 255, 255, 0.08)' }}>
            <div className="d-flex gap-2 align-items-center flex-wrap">
                <small className="text-capitalize opacity-50" style={{ fontSize: '0.7rem', fontWeight: 500 }}>
                    {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
                </small>
                {isDriver && vehicleModel && (
                    <>
                        <span className="opacity-25" style={{ fontSize: '0.7rem' }}>•</span>
                        <span className="badge bg-primary bg-opacity-25 text-primary border border-primary border-opacity-25 px-2 py-0.5 rounded-pill" style={{ fontSize: '0.65rem' }}>
                            <FaCar size={10} className="me-1" /> {vehicleModel}
                        </span>
                    </>
                )}
            </div>
            
            <div className="d-flex flex-wrap gap-1 align-items-center">
                <span className="badge bg-light bg-opacity-10 text-white border border-white border-opacity-10 px-2 py-1 rounded-pill d-flex align-items-center gap-1" style={{ fontSize: '0.65rem' }}>
                    <FaCloud size={10} color="var(--bry-accent)" /> El Tigre 28°C
                </span>
                <span className="badge bg-light bg-opacity-10 text-white border border-white border-opacity-10 px-2 py-1 rounded-pill d-flex align-items-center gap-1" style={{ fontSize: '0.65rem' }}>
                    <FaClock size={10} /> {new Date().toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' })}
                </span>
                {isDriver && (
                    <span className="badge bg-light bg-opacity-10 text-white border border-white border-opacity-10 px-2 py-1 rounded-pill d-flex align-items-center gap-1" onClick={() => setShowCalcModal(true)} style={{ cursor: 'pointer', fontSize: '0.65rem' }}>
                        <FaTachometerAlt size={10} /> {distanceToday?.toFixed(1) || '0'} km hoy
                    </span>
                )}
                {lastLocation && (
                    <span className="badge bg-success bg-opacity-25 text-success border border-success border-opacity-25 px-2 py-1 rounded-pill d-flex align-items-center gap-1" style={{ fontSize: '0.65rem' }}>
                        <FaMapMarkerAlt size={10} /> ({lastLocation.latitude?.toFixed(4)}, {lastLocation.longitude?.toFixed(4)})
                    </span>
                )}
            </div>
        </div>
    </div>
);

export default HeaderSection;
